import { useState, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Row, Col } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { BsArrowRight } from "react-icons/bs";
import _ from 'lodash';

// Custom Component
import Breadcrumb from "../../components/Common/Breadcrumb";
import { PrimaryButton } from "../../components/Button";
import { PlayerCard } from '../../components/Card';
import ErrorAlert from '../../components/Alert/ErrorAlert';
import PaymentConfirmModal from "../../components/Modal/PaymentConfirmModal";

// Images
import { Badminton, PickleBall, User, Users } from '../../assets/images/svg';

// Utils
import { detectMenEvent, detectWomenEvent, convertEventType, convertAgeGroup } from '../../utils/helper';

// API Service
import { createPaymentService } from '../../service/payment.service';
import { removePlayerService } from '../../service/tournament.service';

const TournamentSummary = () => {
    const { state } = useLocation();
    const navigate = useNavigate();

    const [tournament, setTournament] = useState(state?.tournament)
    const [paymentConfirmModal, setPaymentConfirmModal] = useState(false)

    window.history.pushState(null, null, document.URL);
    window.addEventListener('popstate', function (event) {
        navigate('/enter-tournament', {
            state: {
                tournamentId: tournament?.id,
                player_details: tournament?.player_details,
                edit: true
            }
        });
    })

    useEffect(() => {
        if (tournament && tournament?.player_details?.length === 0) {
            navigate('/enter-tournament', {
                state: {
                    tournamentId: tournament?.id
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tournament])

    /**
        * @function createPayment
        * @params
        * @description used to create payment
    */
    const createPayment = async () => {
        try {
            const data = new FormData()
            if (!tournament?.player_details?.every(item => item?.team_name) && tournament?.player_details?.length > 0) {
                data.append('amount', parseFloat(tournament?.player_details[0].event_price).toFixed(2))
            } else if (tournament?.player_details?.every(item => item?.team_name)) {
                data.append('amount', parseFloat(tournament?.team_event_details?.find(item => tournament?.player_details?.find(player => player?.team_event_id)?.team_event_id === item?.id)?.price).toFixed(2))
            }
            data.append('tournament_id', tournament?.id)
            data.append('team_id', tournament?.player_details?.find(item => item?.team_id)?.team_id)
            const result = await createPaymentService(data);
            if (result?.data?.status) {
                window.location.href = result?.data?.data
                handlePaymentConfirmModal()
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
        } catch (error) {
            ErrorAlert(error)
        }
    }

    /**
        * @function removePlayer
        * @params playerId, tournamentId
        * @description used to remove player
    */
    const removePlayer = async (playerId, tournamentId) => {
        try {
            const result = await removePlayerService(tournamentId, playerId);
            if (result?.data?.status) {
                setTournament({
                    ...tournament,
                    player_details: tournament?.player_details.filter(item => item.id !== playerId)
                })
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
        } catch (error) {
            ErrorAlert(error)
        }
    }

    /**
        * @function handlePaymentConfirmModal
        * @params
        * @description set the visibility of payment confirm modal
    */
    const handlePaymentConfirmModal = () => {
        setPaymentConfirmModal(!paymentConfirmModal)
    }

    return <HelmetProvider>
        <Helmet prioritizeSeoTags>
            <title>Tournament Summary | AFA Tournament - Your Gateway to Sporting Excellence!</title>
        </Helmet>
        <Breadcrumb text='Tournament Summary' />
        <p className='fs-25 fw-600 mt-4'>Tournament Summary</p>
        <Row>
            <Col lg={7}>
                <div className='d-flex flex-column align-items-start'>
                    <span className='fs-22 fw-600 text-primary text-capitalize'>{tournament?.name}</span>
                </div>
                <div className='d-flex flex-column align-items-start border rounded px-3 py-2 my-4'>
                    <span className='fs-18 fw-600'>Type of Sports</span>
                    {tournament?.sports_type === "BADMINTON" &&
                        <div className='d-flex flex-column align-items-center text-light bg-primary rounded py-2 px-3 mt-3'>
                            <Badminton className='img-fluid mt-2 mb-2' />
                            <span>Badminton</span>
                        </div>
                    }
                    {tournament?.sports_type === "PICKLEBALL" &&
                        <div className='d-flex flex-column align-items-center text-light bg-primary rounded py-2 px-3 mt-3'>
                            <PickleBall className='img-fluid mt-2 mb-2' />
                            <span>Pickleball</span>
                        </div>
                    }
                </div>

                <div className='d-flex flex-column align-items-start border rounded px-3 py-2 my-4'>
                    <span className='fs-18 fw-600'>Type of Events</span>
                    <div className='d-flex justify-content-between align-items-center mt-2 w-100'>
                        {!tournament?.player_details?.every(item => item?.team_name) &&
                            <div className='d-flex align-items-center'>
                                <User className='me-2' />
                                Individuals Event
                            </div>
                        }
                        {tournament?.player_details?.every(item => item?.team_name) &&
                            <div className='d-flex align-items-center'>
                                <Users className='me-2' />
                                Team Event
                            </div>
                        }
                    </div>
                </div>

                {tournament?.player_details?.every(item => item?.team_name) && tournament?.team_event_details?.length > 0 &&
                    <div className='d-flex flex-column align-items-start border rounded px-3 py-2 my-4'>
                        <span className='fs-18 fw-600'>Team Event</span>
                        <div className='d-flex justify-content-between align-items-center mt-2 w-100'>
                            <div className='d-flex align-items-center'>
                                <Users className='me-2' />
                                {tournament?.team_event_details?.find(item => tournament?.player_details?.find(player => player?.team_name)?.team_event_id === item?.id)?.event_name}
                            </div>
                        </div>
                    </div>
                }

                {tournament?.player_details?.every(item => item?.team_name) &&
                    <div className='d-flex flex-column align-items-start border rounded px-3 py-2 my-4'>
                        <span className='fs-18 fw-600'>Team Name</span>
                        <div className='d-flex justify-content-between align-items-center mt-2 w-100'>
                            <div className='d-flex align-items-center'>
                                <Users className='me-2' />
                                {tournament?.player_details?.find(item => item?.team_name)?.team_name}
                            </div>
                        </div>
                    </div>
                }

                {tournament?.player_details?.length > 0 && !tournament?.player_details?.every(item => item?.team_name) &&
                    <>
                        <div className='d-flex flex-column align-items-start border rounded px-3 py-2 my-4'>
                            <span className='fs-18 fw-600'>Categories</span>
                            <div className='d-flex flex-wrap align-items-center mt-2'>
                                <div className='bg-primary me-2 px-3 py-1 rounded mt-2'>
                                    <div className='d-flex align-items-center text-primary'>
                                        {tournament?.player_details?.length > 0 && detectMenEvent({
                                            event: tournament?.player_details[0]?.event_type,
                                            color: 'white',
                                            className: 'me-2'
                                        })}
                                        {tournament?.player_details?.length > 0 && detectWomenEvent({
                                            event: tournament?.player_details[0]?.event_type,
                                            color: 'white',
                                            className: 'me-2'
                                        })}
                                        {tournament?.player_details?.length > 0 &&
                                            <div className='d-flex flex-column align-items-center text-light'>
                                                <span>{convertEventType(tournament?.player_details[0]?.event_type)}</span>
                                                <span>({convertAgeGroup(tournament?.player_details[0]?.age_group, tournament?.player_details[0]?.age_value)})</span>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='d-flex flex-column align-items-start border rounded my-3'>
                            <span className='fs-18 fw-600'>Players ({tournament?.player_details?.length || 0})</span>
                            {tournament?.player_details?.length > 0 && tournament?.player_details?.map((item, index) => {
                                return <PlayerCard
                                    key={index}
                                    index={index}
                                    item={item}
                                    tournament={tournament}
                                    removePlayer={removePlayer}
                                />
                            })}
                        </div>
                    </>
                }

                {tournament?.player_details?.every(item => item?.team_name) &&
                    <>
                        {tournament?.player_details?.length > 0 && _(tournament?.player_details)?.groupBy('event_type')?.map((items, event_type) => ({
                            event_type,
                            age_groups: _(items)?.groupBy('age_group')?.map((ageGroupItems, age_group) => ({
                                age_group,
                                age_values: _.uniq(ageGroupItems?.map(i => i?.age_value))
                            })).value()
                        })).value()?.length > 0 && _(tournament?.player_details)?.groupBy('event_type')?.map((items, event_type) => ({
                            event_type,
                            age_groups: _(items)?.groupBy('age_group')?.map((ageGroupItems, age_group) => ({
                                age_group,
                                age_values: _.uniq(ageGroupItems?.map(i => i?.age_value))
                            })).value()
                        })).value()?.map((eventItem, eventIndex) => {
                            return <div key={`event${eventIndex}`}>
                                {eventItem?.age_groups?.length > 0 && eventItem?.age_groups?.map((ageGroupItem, ageGroupIndex) => {
                                    return <div key={`age-group${ageGroupIndex}`}>
                                        {ageGroupItem?.age_values?.length > 0 && ageGroupItem?.age_values?.map((ageValueItem, ageValueIndex) => {
                                            return <div className='d-flex flex-column align-items-start w-100' key={`age-value${ageValueIndex}`}>
                                                <div className='d-flex flex-column align-items-start border rounded px-3 py-2 my-3 w-100'>
                                                    <span className='fs-18 fw-600'>Categories</span>
                                                    <div className='d-inline-flex align-items-center text-primary bg-primary me-2 px-3 py-1 rounded mt-2'>
                                                        {detectMenEvent({
                                                            event: eventItem?.event_type,
                                                            color: 'white',
                                                            className: 'me-2'
                                                        })}
                                                        {detectWomenEvent({
                                                            event: eventItem?.event_type,
                                                            color: 'white',
                                                            className: 'me-2'
                                                        })}
                                                        <div className='d-flex flex-column align-items-center text-light'>
                                                            <span>{convertEventType(eventItem?.event_type)}</span>
                                                            <span>({convertAgeGroup(ageGroupItem?.age_group, ageValueItem)})</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='d-flex flex-column align-items-start rounded my-3 w-100'>
                                                    <span className='fs-18 fw-600'>Players ({tournament?.player_details?.filter(item => item?.event_type === eventItem?.event_type && item?.age_group === ageGroupItem?.age_group && item?.age_value === ageValueItem)?.length || 0})</span>
                                                    {tournament?.player_details?.filter(item => item?.event_type === eventItem?.event_type && item?.age_group === ageGroupItem?.age_group && item?.age_value === ageValueItem)?.length > 0 && tournament?.player_details?.filter(item => item?.event_type === eventItem?.event_type && item?.age_group === ageGroupItem?.age_group && item?.age_value === ageValueItem)?.map((item, index) => {
                                                        return <PlayerCard
                                                            key={index}
                                                            index={index}
                                                            item={item}
                                                            tournament={tournament}
                                                            removePlayer={removePlayer}
                                                        />
                                                    })}
                                                </div>
                                            </div>
                                        })}
                                    </div>
                                })}
                            </div>
                        })}
                    </>
                }
            </Col>
            <Col lg={5} className='mb-3 mb-lg-0'>
                <div className='d-flex flex-column align-items-start bg-light border rounded p-3'>
                    <span className='fs-22 fw-600 text-secondary'>Details</span>
                    {!tournament?.player_details?.every(item => item?.team_name) &&
                        <div className='d-flex justify-content-between align-items-center w-100 mt-3 fw-600'>
                            <span>Original Price</span>
                            {tournament?.player_details?.length > 0 && <span>RM{parseFloat(tournament?.player_details[0].event_price).toFixed(2)}</span>}
                        </div>
                    }
                    {tournament?.player_details?.every(item => item?.team_name) &&
                        <div className='d-flex justify-content-between align-items-center w-100 mt-3 fw-600'>
                            <span>Team Event Price</span>
                            <span>RM{parseFloat(tournament?.team_event_details?.find(item => tournament?.player_details?.find(player => player?.team_event_id)?.team_event_id === item?.id)?.price).toFixed(2)}</span>
                        </div>
                    }
                    <div className='d-flex justify-content-between align-items-center w-100 mt-3 fw-600'>
                        <span>Players</span>
                        <span>{tournament?.player_details?.length || 0}</span>
                    </div>
                    {!tournament?.player_details?.every(item => item?.team_name) && tournament?.player_details &&
                        <div className='d-flex justify-content-between align-items-center w-100 mt-3 fw-600'>
                            <span>Total Price</span>
                            {tournament?.player_details?.length > 0 && <span>RM{parseFloat(tournament?.player_details[0].event_price).toFixed(2)}</span>}
                        </div>
                    }
                    {tournament?.player_details?.every(item => item?.team_name) && tournament?.player_details &&
                        <div className='d-flex justify-content-between align-items-center w-100 mt-3 fw-600'>
                            <span>Total Price</span>
                            <span>RM{parseFloat(tournament?.team_event_details?.find(item => tournament?.player_details?.find(player => player?.team_event_id)?.team_event_id === item?.id)?.price).toFixed(2)}</span>
                        </div>
                    }
                    <PrimaryButton className='fs-14 fw-500 my-3' onClick={handlePaymentConfirmModal}>
                        Complete Checkout
                        <BsArrowRight className='ms-2' />
                    </PrimaryButton>
                </div>
            </Col>
        </Row>

        {/* Payment Confirm Modal */}
        <PaymentConfirmModal
            show={paymentConfirmModal}
            handleClose={handlePaymentConfirmModal}
            createPayment={createPayment}
            tournament={tournament}
            size='lg'
        />
    </HelmetProvider>
};

export default TournamentSummary;